import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
  Shield, Search, Database, Clock, CheckCircle, XCircle, GitMerge,
  Plus, ArrowLeft, AlertTriangle, TrendingUp, Pill, Building2, LogOut
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getSimilarityColor(score) {
  if (score >= 95) return 'bg-green-600';
  if (score >= 70) return 'bg-amber-500';
  if (score >= 50) return 'bg-blue-500';
  return 'bg-slate-500';
}

function getSimilarityLabel(score) {
  if (score >= 95) return 'Auto-Merge';
  if (score >= 70) return 'Review';
  if (score >= 50) return 'Possible';
  return 'Low';
}

function AdminMedicines() {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [stats, setStats] = useState(null);
  const [pendingMedicines, setPendingMedicines] = useState([]);
  const [globalMedicines, setGlobalMedicines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchGlobal, setSearchGlobal] = useState('');
  const [statusFilter, setStatusFilter] = useState('pending');
  const [showActionModal, setShowActionModal] = useState(false);
  const [selectedPending, setSelectedPending] = useState(null);
  const [selectedAction, setSelectedAction] = useState('');
  const [selectedMergeTarget, setSelectedMergeTarget] = useState('');
  const [processing, setProcessing] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newMedicine, setNewMedicine] = useState({
    brand_name: '', salt_composition: '', hsn_code: '', manufacturer: '', category: 'General', schedule_type: 'none'
  });

  const fetchData = useCallback(async function() {
    try {
      const [statsRes, pendingRes, globalRes] = await Promise.all([
        axios.get(API + '/admin/medicine-stats'),
        axios.get(API + '/admin/pending-medicines?status_filter=' + statusFilter),
        axios.get(API + '/admin/global-medicines')
      ]);
      setStats(statsRes.data);
      setPendingMedicines(pendingRes.data);
      setGlobalMedicines(globalRes.data);
    } catch (error) {
      if (error.response && error.response.status === 403) {
        toast.error('Access denied');
        navigate('/admin');
      }
    } finally {
      setLoading(false);
    }
  }, [statusFilter, navigate]);

  useEffect(function() { fetchData(); }, [fetchData]);

  async function handleAction() {
    if (!selectedPending || !selectedAction) return;
    if (selectedAction === 'merge' && !selectedMergeTarget) {
      toast.error('Select a medicine to merge with');
      return;
    }
    setProcessing(true);
    try {
      await axios.post(API + '/admin/pending-medicines/' + selectedPending.id + '/action', {
        action: selectedAction,
        global_medicine_id: selectedAction === 'merge' ? selectedMergeTarget : null
      });
      toast.success('Action completed');
      setShowActionModal(false);
      setSelectedPending(null);
      fetchData();
    } catch (e) {
      toast.error('Action failed');
    }
    setProcessing(false);
  }

  async function addGlobalMedicine(e) {
    e.preventDefault();
    if (!newMedicine.brand_name) return;
    setProcessing(true);
    try {
      await axios.post(API + '/admin/global-medicines', newMedicine);
      toast.success('Medicine added');
      setShowAddModal(false);
      setNewMedicine({ brand_name: '', salt_composition: '', hsn_code: '', manufacturer: '', category: 'General', schedule_type: 'none' });
      fetchData();
    } catch (e) {
      toast.error('Failed to add');
    }
    setProcessing(false);
  }

  function openActionModal(pending) {
    setSelectedPending(pending);
    const matches = pending.similarity_matches || [];
    if (matches.length > 0 && matches[0].similarity_score >= 70) {
      setSelectedAction('merge');
      setSelectedMergeTarget(matches[0].global_medicine_id);
    } else {
      setSelectedAction('approve');
      setSelectedMergeTarget('');
    }
    setShowActionModal(true);
  }

  function handleLogout() {
    logout();
    navigate('/login');
  }

  const filteredGlobal = globalMedicines.filter(function(m) {
    if (!searchGlobal) return true;
    return m.brand_name.toLowerCase().includes(searchGlobal.toLowerCase());
  });

  if (loading) {
    return <div className="min-h-screen bg-slate-900 flex items-center justify-center"><div className="text-white">Loading...</div></div>;
  }

  // Prepare pending items for render
  const pendingItems = pendingMedicines.map(function(p) {
    const statusColor = p.status === 'pending' ? 'bg-amber-600' : p.status === 'approved' ? 'bg-green-600' : p.status === 'merged' ? 'bg-blue-600' : 'bg-red-600';
    return { ...p, statusColor };
  });

  // Prepare match items for action modal
  const matchItems = selectedPending && selectedPending.similarity_matches ? selectedPending.similarity_matches.slice(0, 5) : [];

  return (
    <div className="min-h-screen bg-slate-900 p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" className="border-slate-600 text-slate-300" onClick={function() { navigate('/admin'); }}>
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <Database className="w-8 h-8 text-purple-400" />
          <div>
            <h1 className="text-2xl font-bold text-white">Global Medicine Database</h1>
            <p className="text-slate-400 text-sm">Self-healing catalog with ML deduplication</p>
          </div>
        </div>
        <Button variant="outline" className="border-red-600 text-red-400" onClick={handleLogout}>
          <LogOut className="w-4 h-4 mr-2" /> Logout
        </Button>
      </div>

      {stats && (
        <div className="grid grid-cols-5 gap-4 mb-6">
          <Card className="bg-slate-800 border-slate-700">
            <CardContent className="p-4 text-center">
              <Database className="w-6 h-6 text-purple-400 mx-auto mb-2" />
              <p className="text-2xl font-bold text-white">{stats.global_medicines_count}</p>
              <p className="text-xs text-slate-400">Global</p>
            </CardContent>
          </Card>
          <Card className="bg-amber-900/30 border-amber-700">
            <CardContent className="p-4 text-center">
              <Clock className="w-6 h-6 text-amber-400 mx-auto mb-2" />
              <p className="text-2xl font-bold text-amber-400">{stats.pending_approvals}</p>
              <p className="text-xs text-slate-400">Pending</p>
            </CardContent>
          </Card>
          <Card className="bg-green-900/30 border-green-700">
            <CardContent className="p-4 text-center">
              <CheckCircle className="w-6 h-6 text-green-400 mx-auto mb-2" />
              <p className="text-2xl font-bold text-green-400">{stats.approved_count}</p>
              <p className="text-xs text-slate-400">Approved</p>
            </CardContent>
          </Card>
          <Card className="bg-blue-900/30 border-blue-700">
            <CardContent className="p-4 text-center">
              <GitMerge className="w-6 h-6 text-blue-400 mx-auto mb-2" />
              <p className="text-2xl font-bold text-blue-400">{stats.merged_count}</p>
              <p className="text-xs text-slate-400">Merged</p>
            </CardContent>
          </Card>
          <Card className="bg-red-900/30 border-red-700">
            <CardContent className="p-4 text-center">
              <XCircle className="w-6 h-6 text-red-400 mx-auto mb-2" />
              <p className="text-2xl font-bold text-red-400">{stats.rejected_count}</p>
              <p className="text-xs text-slate-400">Rejected</p>
            </CardContent>
          </Card>
        </div>
      )}

      <Tabs defaultValue="pending" className="space-y-4">
        <TabsList className="bg-slate-800 border-slate-700">
          <TabsTrigger value="pending" className="data-[state=active]:bg-slate-700">
            <Clock className="w-4 h-4 mr-2" /> Pending ({pendingMedicines.length})
          </TabsTrigger>
          <TabsTrigger value="global" className="data-[state=active]:bg-slate-700">
            <Database className="w-4 h-4 mr-2" /> Global ({globalMedicines.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="pending">
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-white">Pending Approvals</CardTitle>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-32 bg-slate-700 border-slate-600 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="merged">Merged</SelectItem>
                    <SelectItem value="all">All</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[450px]">
                <div className="space-y-3">
                  {pendingItems.length === 0 && (
                    <div className="text-center py-12 text-slate-500">
                      <CheckCircle className="w-12 h-12 mx-auto mb-4 text-green-500" />
                      <p>No pending approvals</p>
                    </div>
                  )}
                  {pendingItems.map(function(item) {
                    const matches = item.similarity_matches || [];
                    return (
                      <div key={item.id} className="p-4 bg-slate-700 rounded-lg">
                        <div className="flex justify-between items-start">
                          <div>
                            <div className="flex items-center gap-2">
                              <Pill className="w-5 h-5 text-mint-400" />
                              <h3 className="font-semibold text-white">{item.brand_name}</h3>
                              <Badge className={item.statusColor}>{item.status}</Badge>
                            </div>
                            <p className="text-slate-400 text-sm mt-1">{item.salt_composition}</p>
                            <p className="text-xs text-slate-500 mt-1">From: {item.tenant_name} | {item.category}</p>
                          </div>
                          {item.status === 'pending' && (
                            <Button size="sm" className="bg-purple-600" onClick={function() { openActionModal(item); }}>Review</Button>
                          )}
                        </div>
                        {matches.length > 0 && (
                          <div className="mt-3 pt-3 border-t border-slate-600">
                            <p className="text-xs text-slate-400 mb-2">Similar medicines:</p>
                            {matches.slice(0, 2).map(function(m, i) {
                              return (
                                <div key={i} className="flex items-center justify-between p-2 bg-slate-600/50 rounded mb-1">
                                  <span className="text-sm text-white">{m.brand_name}</span>
                                  <Badge className={getSimilarityColor(m.similarity_score)}>{m.similarity_score}%</Badge>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="global">
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-white">Global Database</CardTitle>
                <div className="flex items-center gap-2">
                  <Input placeholder="Search..." value={searchGlobal} onChange={function(e) { setSearchGlobal(e.target.value); }} className="w-48 bg-slate-700 border-slate-600 text-white" />
                  <Button className="bg-green-600" onClick={function() { setShowAddModal(true); }}>
                    <Plus className="w-4 h-4 mr-1" /> Add
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[450px]">
                <div className="space-y-2">
                  {filteredGlobal.length === 0 && (
                    <div className="text-center py-12 text-slate-500">No medicines</div>
                  )}
                  {filteredGlobal.map(function(med) {
                    return (
                      <div key={med.id} className="p-3 bg-slate-700 rounded-lg flex items-center justify-between">
                        <div>
                          <p className="font-medium text-white">{med.brand_name}</p>
                          <p className="text-sm text-slate-400">{med.salt_composition}</p>
                        </div>
                        <Badge className="bg-slate-600">{med.usage_count} uses</Badge>
                      </div>
                    );
                  })}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={showActionModal} onOpenChange={setShowActionModal}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle>Review Medicine</DialogTitle>
          </DialogHeader>
          {selectedPending && (
            <div className="space-y-4">
              <div className="p-3 bg-slate-700 rounded-lg">
                <h3 className="font-semibold">{selectedPending.brand_name}</h3>
                <p className="text-slate-400 text-sm">{selectedPending.salt_composition}</p>
              </div>
              {matchItems.length > 0 && (
                <div>
                  <p className="text-sm text-amber-400 mb-2">Similar medicines found - consider merging:</p>
                  <div className="space-y-2 max-h-32 overflow-y-auto">
                    {matchItems.map(function(m, i) {
                      const isSelected = selectedMergeTarget === m.global_medicine_id;
                      return (
                        <div key={i} className={'p-2 rounded cursor-pointer ' + (isSelected ? 'bg-purple-600/30 border border-purple-500' : 'bg-slate-600/50')} onClick={function() { setSelectedMergeTarget(m.global_medicine_id); setSelectedAction('merge'); }}>
                          <div className="flex justify-between">
                            <span className="text-sm">{m.brand_name}</span>
                            <Badge className={getSimilarityColor(m.similarity_score)}>{m.similarity_score}%</Badge>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              <div className="grid grid-cols-3 gap-2">
                <Button variant={selectedAction === 'approve' ? 'default' : 'outline'} className={selectedAction === 'approve' ? 'bg-green-600' : ''} onClick={function() { setSelectedAction('approve'); setSelectedMergeTarget(''); }}>
                  <CheckCircle className="w-4 h-4 mr-1" /> Approve
                </Button>
                <Button variant={selectedAction === 'merge' ? 'default' : 'outline'} className={selectedAction === 'merge' ? 'bg-blue-600' : ''} onClick={function() { setSelectedAction('merge'); }} disabled={matchItems.length === 0}>
                  <GitMerge className="w-4 h-4 mr-1" /> Merge
                </Button>
                <Button variant={selectedAction === 'reject' ? 'default' : 'outline'} className={selectedAction === 'reject' ? 'bg-red-600' : ''} onClick={function() { setSelectedAction('reject'); setSelectedMergeTarget(''); }}>
                  <XCircle className="w-4 h-4 mr-1" /> Reject
                </Button>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={function() { setShowActionModal(false); }}>Cancel</Button>
            <Button className="bg-purple-600" onClick={handleAction} disabled={processing || !selectedAction}>Confirm</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle>Add Global Medicine</DialogTitle>
          </DialogHeader>
          <form onSubmit={addGlobalMedicine} className="space-y-3">
            <div>
              <Label>Brand Name *</Label>
              <Input value={newMedicine.brand_name} onChange={function(e) { setNewMedicine({ ...newMedicine, brand_name: e.target.value }); }} className="bg-slate-700 border-slate-600" />
            </div>
            <div>
              <Label>Salt Composition</Label>
              <Input value={newMedicine.salt_composition} onChange={function(e) { setNewMedicine({ ...newMedicine, salt_composition: e.target.value }); }} className="bg-slate-700 border-slate-600" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>Category</Label>
                <Select value={newMedicine.category} onValueChange={function(v) { setNewMedicine({ ...newMedicine, category: v }); }}>
                  <SelectTrigger className="bg-slate-700 border-slate-600"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="General">General</SelectItem>
                    <SelectItem value="Antibiotic">Antibiotic</SelectItem>
                    <SelectItem value="Analgesic">Analgesic</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Schedule</Label>
                <Select value={newMedicine.schedule_type} onValueChange={function(v) { setNewMedicine({ ...newMedicine, schedule_type: v }); }}>
                  <SelectTrigger className="bg-slate-700 border-slate-600"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    <SelectItem value="H1">H1</SelectItem>
                    <SelectItem value="X">X</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={function() { setShowAddModal(false); }}>Cancel</Button>
              <Button type="submit" className="bg-green-600" disabled={processing}>Add</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default AdminMedicines;
